/**
 * Stages AI-proposed edits as ProseMirror decorations the user accepts or
 * rejects inline. Plugin state is the single source of truth for proposal
 * positions; ProseMirror's mapping auto-remaps them through subsequent edits
 * so the proposal stays anchored to the right content while the user keeps
 * typing.
 *
 * Decorations are view-layer only — they never enter the Yjs doc, so other
 * Liveblocks participants don't see a user's pending AI proposals until they
 * accept them.
 */
import type { EditorOperation } from '@seen/ai-agents'
import type {
  CitedSource,
  SuggestionProvenance,
} from '@seen/core/models/compositionNote'
import { effectiveSuggestionHtml } from '@seen/core/models/suggestionStagingBasic'
import { type Editor, Extension } from '@tiptap/core'
import type { Node as PMNode } from '@tiptap/pm/model'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import {
  Decoration,
  type DecorationAttrs,
  DecorationSet,
} from '@tiptap/pm/view'
import { orderBy } from 'lodash-es'
import {
  captureFingerprint,
  captureRelAnchor,
  type RelAnchor,
  resolveRelAnchor,
  type StructuralFingerprint,
} from '#src/components/rich-text-editor/aiProposalRelAnchor.ts'
import { isWholeDocReplace } from '#src/components/rich-text-editor/aiProposalTransactions.ts'
import { splitFillReplacement } from '#src/components/rich-text-editor/fillReplacement.ts'
import {
  type AIProposalsStorage,
  createIslandRegistry,
} from '#src/components/rich-text-editor/islandRegistry.ts'
import type { SuggestionWidgetShells } from '#src/components/rich-text-editor/suggestionWidgetShells.ts'

export type ProposalKind = 'insert' | 'replace'

/**
 * How buildDecorations should preview a proposal. Set by the resolver from the
 * op type so the render dispatcher never re-discovers op semantics (no flags,
 * no HTML-sniffing). Add a variant here + set it in resolveOperation to give a
 * new op type bespoke rendering.
 */
export type ProposalRender =
  | { type: 'default' } // kind-driven: strikethrough over [from,to] if replace, + insert-widget preview
  | { type: 'taskItem' } // like default, but the insert widget wraps an orphan <li> in a taskList
  | { type: 'choiceOption'; value: string } // inline-highlight the matching option button, plus, with `inlineChoiceSources`, the source line under its text line when cited; paints nothing (never the default preview) if the choice field is already selected

/** The render-relevant payload a resolved op produces. */
export interface ProposalRenderSpec {
  kind: ProposalKind
  /** Anchor position. For `replace`, the start of the replaced range. */
  from: number
  /** End of replaced range. Equal to `from` for `insert`. */
  to: number
  /** Sanitized HTML for the proposed new content. */
  htmlContent: string
  /** Optional human-readable summary surfaced in the hover popover. */
  label?: string
  render: ProposalRender
  /* The resource the model cited for this suggestion, and its quoted excerpt of
   * that resource. Copied off the op by resolveOperation so a rehydrated
   * proposal carries it too. Shown BESIDE the proposal, never inside
   * `htmlContent` — an accept writes the content and discards the citation, so
   * it stays out of the record. */
  citation?: CitedSource
  /* Where this suggestion is grounded, filled in by the server and copied off
   * the op by resolveOperation alongside `citation` — one entry per passage the
   * suggestion rests on. Takes precedence over `citation` when both are
   * present. */
  sourceProvenance?: SuggestionProvenance[]
  /* Staged under the fill-only rule, copied off the op like the citation. A
   * replace then shows only what its replacement drops and adds; see
   * `splitFillReplacement`. */
  fillOnly?: true
}

export interface AIProposal extends ProposalRenderSpec {
  id: string
  toolCallId: string
  /* The clinician's island edit of this suggestion, mirrored off the staging
   * record when the proposal is painted. Named for the record field it carries
   * so a proposal satisfies `effectiveSuggestionHtml`'s record shape: display
   * and island-seed surfaces read the pair through that accessor, never
   * `htmlContent` alone, which stays the AI's untouched proposal (what Restore
   * original reverts to). */
  editedContent?: string
  /**
   * Yjs relative-position anchor captured at stage time (Strategy A), present
   * only when the editor is collaborative (a y-prosemirror binding exists).
   * Survives a whole-doc ReplaceStep — the reconciler re-derives from/to from it
   * post-settle instead of dropping the proposal. Undefined for a non-collab
   * editor, where the proposal keeps the legacy drop-on-collapse behavior.
   */
  relAnchor?: RelAnchor
  /**
   * Structural fingerprint (container/type survived, NOT exact text) captured
   * alongside `relAnchor`; gates whether a re-derived position is trusted.
   */
  fingerprint?: StructuralFingerprint
  /**
   * True while positions are known-stale: a whole-doc replace collapsed the PM
   * anchors and the proposal is awaiting post-settle re-derivation. Its from/to
   * still hold the pre-collapse spot so the decoration stays painted (clamped)
   * — the anti-flash window — until `settleAIProposals` repositions it or the
   * reconciler re-places it from its op.
   */
  stale?: boolean
  /**
   * The op this proposal itself resolved from, carried (not re-derived) so a
   * later revision of this proposal (`supersedeAIProposal`) can build a
   * self-contained persisted op from it. Positions are NOT stored on it — the
   * op is, since positions drift and the op does not.
   */
  op?: EditorOperation
  // Id of the proposal this one replaced via `supersedeAIProposal`. Distinct
  // from `stale`: `stale` means "PM positions are known-invalid pending
  // reconciliation" and is cleared on every successful remap/settle;
  // `supersedes` is set once at stage time (supersession) and never cleared.
  supersedes?: string
}

// Single construction site for an AIProposal from a resolved render spec.
export function toAIProposal(
  id: string,
  toolCallId: string,
  spec: ProposalRenderSpec,
  labelFallback?: string,
  editedContent?: string,
  op?: EditorOperation
): AIProposal {
  return {
    ...spec,
    id,
    toolCallId,
    label: spec.label ?? labelFallback,
    editedContent,
    op,
  }
}

export interface AIChoiceProposalSpec {
  id: string
  value: string
  label: string | null
}

interface PluginState {
  proposals: Map<string, AIProposal>
  decorations: DecorationSet
}

// A post-settle reposition for one stale proposal, to its re-derived PM
// positions. A proposal whose anchor did NOT re-derive gets no entry and stays
// stale — see `settleAIProposals`.
interface SettleUpdate {
  from: number
  to: number
}

interface Meta {
  add?: AIProposal[]
  supersede?: {
    supersededId: string
    replacement: AIProposal
    // The replacement is anchored at its own range, not the superseded one's.
    ownRange?: true
  }
  remove?: string[]
  clear?: true
  settle?: Map<string, SettleUpdate>
  edited?: { id: string; editedContent: string | undefined }
}

export const AI_PROPOSALS_PLUGIN_KEY = new PluginKey<PluginState>('aiProposals')

/* Every pending suggestion paints as its OWN card. The border and the block
 * margin are load-bearing, not decoration: two suggestions staged at adjacent
 * anchors otherwise merge into one continuous green run, hiding the fact that
 * each is separately accept/reject-able. The hover border marks which card the
 * floating popover is acting on, and the border tokens match the island's so
 * the preview→island swap doesn't shift the card's outline. */
export const AI_PROPOSAL_INSERT_CLASS =
  'ai-proposal-insert my-0.5 block rounded-sm border border-green-300 bg-green-100 px-0.5 text-green-900 hover:border-green-500 dark:border-green-800 dark:bg-green-900/30 dark:text-green-200 dark:hover:border-green-600'

/* The widget shell the note editor's decoration hosts, which is NOT the tinted
 * card: the shell holds the card AND the source excerpt that sits under it, and
 * only the card wears the tint. The two are separate elements because the
 * excerpt is not part of the proposal -- an accept writes the card's text and
 * drops the excerpt -- so a citation inside the tint read as text about to be
 * written, and its chip's own border landed on the card's. Untinted and
 * unpadded, so the card's own block margin still collapses through it and two
 * suggestions staged at adjacent anchors stay as far apart as before.
 *
 * `block`, and the hidden trailing break that has to come with it, belong to
 * this shell rather than the card: the shell is the widget's own DOM node, so
 * the generated break and separator are ITS siblings and a selector on the
 * nested card never reaches them.
 *
 * An inline-level card is full width, so it can never share the anchor text's
 * line and always wraps -- which puts the anchor position (the end of that
 * text) on a LINE BREAK. A DOM point at a wrap is ambiguous, and the browser
 * paints it on whichever side the caret arrived from: arriving forward through
 * the anchor text painted it at the start of the card's own line, a caret as
 * tall as the card that read as sitting BELOW it, while typing there appended
 * to the very sentence the card proposes to replace. Caret affinity is not
 * settable from script, so the only fix is to stop the position being a wrap at
 * all -- which is what a block box does, by ending the text's inline formatting
 * context. Measured in Chrome: the caret then paints at the end of the anchor
 * text and the card-tall rendering is gone.
 *
 * A block box is also what forces ProseMirror's `ProseMirror-trailingBreak` --
 * appended by addTextblockHacks when a widget is a textblock's last view child
 * -- onto its own anonymous line under the card, and that line is a dead strip:
 * it has no document size, so a click in it maps back to the widget's own
 * anchor and the caret jumps up. Hiding the break collapses the strip, and
 * AISuggestionTrailingParagraphExtension still supplies a real paragraph below
 * a note-ending card to click into.
 *
 * BOTH generated nodes have to go, and through a GENERAL sibling (`~`) rather
 * than an adjacent one: ProseMirror appends them as the textblock's last
 * children, adjacent to the card only when the card is itself last. The break
 * comes paired with an `img.ProseMirror-separator`, which prosemirror-view's own
 * stylesheet pins to `display: inline !important` -- so the strip survives on
 * the image's line box alone unless the image is hidden too, and only an
 * important utility outranks that rule. Restricting to those two generated
 * classes keeps a clinician's own hard breaks after the card visible. */
export const AI_PROPOSAL_WIDGET_CLASS =
  'ai-proposal-widget my-0.5 block [&~br.ProseMirror-trailingBreak]:hidden [&~img.ProseMirror-separator]:hidden!'

/* The standalone card the care-plan questionnaire draws, sharing every token
 * with AI_PROPOSAL_INSERT_CLASS above except its box treatment: this card
 * wraps its own open handler around the whole card, so it can afford the
 * roomier radius and padding that would be a dead click strip on the note
 * editor's widget shell, whose click-to-edit is bound to the preview nested
 * inside it. */
export const AI_PROPOSAL_CARD_CLASS =
  'ai-proposal-insert my-0.5 block rounded-lg border border-green-300 bg-green-100 p-4 text-green-900 hover:border-green-500 dark:border-green-800 dark:bg-green-900/30 dark:text-green-200 dark:hover:border-green-600'

/* Text a proposal adds to the end of a line that already holds the clinician's
 * words. Inline, unlike the card: the card would box the whole line, existing
 * words included, while only the addition is the suggestion. */
export const AI_PROPOSAL_ADDITION_CLASS =
  'ai-proposal-addition rounded-sm bg-green-100 text-green-900 box-decoration-clone dark:bg-green-900/30 dark:text-green-200'

export const AI_PROPOSAL_REMOVAL_CLASS =
  'ai-proposal-removal bg-red-100 dark:bg-red-900/30 text-red-900 dark:text-red-200 line-through rounded-sm px-0.5'

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    aiProposals: {
      addAIProposals: (proposals: AIProposal[]) => ReturnType
      supersedeAIProposal: (
        supersededId: string,
        replacement: AIProposal,
        options?: { ownRange?: boolean }
      ) => ReturnType
      acceptAIProposal: (id: string) => ReturnType
      rejectAIProposal: (id: string) => ReturnType
      rejectAllAIProposals: () => ReturnType
      clearAIProposalsByToolCall: (toolCallId: string) => ReturnType
      settleAIProposals: () => ReturnType
      setAIProposalEditedContent: (
        id: string,
        editedContent: string | undefined
      ) => ReturnType
    }
  }
  // Optional: editors without this extension have no aiProposals storage, so
  // island-registry access (getIslandRegistry) reads through and returns null.
  interface Storage {
    aiProposals?: AIProposalsStorage
  }
}

export interface AIProposalsOptions {
  /* Factory the React host injects to mount pending insert content through the
   * host-owned React preview root (the real node views — design §1.2). Null
   * (the default) is a bare editor with no React host: it paints no insert
   * widgets rather than wrong ones, and no innerHTML fallback exists anywhere. */
  createShells: ((editor: Editor) => SuggestionWidgetShells) | null
  /* Paints a cited choice pick's source under the line holding its field (the
   * ai-chat-choice-inline-source flag). Off — the default — a choice pick paints
   * only the option highlight, exactly as before. Read once when the plugin is
   * built, like `createShells`, so it is fixed for the editor's lifetime. */
  inlineChoiceSources: boolean
}

export const AIProposalsExtension = Extension.create<
  AIProposalsOptions,
  AIProposalsStorage
>({
  name: 'aiProposals',

  addOptions() {
    return { createShells: null, inlineChoiceSources: false }
  },

  /* One island registry per editor (design §2.a). Holds which suggestion
   * islands are open for local editing plus their live controllers; the popover,
   * the widget shell, and (2.b) the accept pipeline reach it via
   * `editor.storage.aiProposals.islandRegistry`. Storage is created before
   * plugins, so the shell manager can read it in addProseMirrorPlugins. */
  addStorage() {
    return { islandRegistry: createIslandRegistry() }
  },

  addProseMirrorPlugins() {
    const shells = this.options.createShells?.(this.editor) ?? null
    const inlineChoiceSources = this.options.inlineChoiceSources
    return [
      new Plugin<PluginState>({
        key: AI_PROPOSALS_PLUGIN_KEY,
        state: {
          init: () => ({
            proposals: new Map(),
            decorations: DecorationSet.empty,
          }),
          apply(tr, prev, _oldState, newState) {
            const meta = tr.getMeta(AI_PROPOSALS_PLUGIN_KEY) as Meta | undefined

            /* Fast path: no AI changes, no pending proposals. This is every
             * keystroke for the 99% of editor time when the AI hasn't proposed
             * anything. Skip the remap and decoration rebuild. */
            if (!meta && prev.proposals.size === 0) {
              return prev
            }

            let proposals = prev.proposals
            if (tr.docChanged && proposals.size > 0) {
              // A whole-doc ReplaceStep is the collapse case relative anchors
              // recover from; an ordinary local edit is not (PM mapResult is
              // authoritative there, and a genuine local deletion SHOULD drop).
              const wholeDoc = isWholeDocReplace(tr)
              const remapped = new Map<string, AIProposal>()
              for (const [id, p] of proposals) {
                /* Use mapResult so we can detect anchors the step destroyed.
                 * y-prosemirror's sync plugin reports any Yjs-origin change
                 * (cmd+z via the Liveblocks UndoManager, a remote co-editor
                 * edit, a sync tick) as ONE whole-document ReplaceStep
                 * (StepMap `[0, oldSize, newSize]`). Under that map every
                 * interior position collapses, so the old `map()` calls
                 * silently re-anchored every pending proposal to position 0,
                 * stacking all insert widgets above the H1. */
                /* A collapsed anchor is a POINT, not a range: mapping its two
                 * ends inward asks where content that does not exist went, and
                 * an insertion at that exact point answers differently for each
                 * end — `from` lands after the new content, `to` before it. The
                 * ends diverge, the range reads as invalid, and the proposal is
                 * dropped, which is what silently unpainted an undecided
                 * suggestion when two shared an insertion point (two
                 * `insertAtEnd` ops resolve to the same caret) and one was
                 * accepted. Map the point ONCE so it rides adjacent insertions
                 * instead of dying to them, matching `side: 1` on its widget and
                 * the relative-anchor path's own rule that a caret stays
                 * collapsed and lands after text typed at it. */
                const collapsed = p.from === p.to
                const fromR = tr.mapping.mapResult(p.from, 1)
                const toR = collapsed ? fromR : tr.mapping.mapResult(p.to, -1)
                const from = fromR.pos
                const to = toR.pos

                // `deletedAcross` is the primary guard: the step deleted
                // content spanning the anchor — still the right question for a
                // collapsed anchor, whose surrounding content can be deleted
                // out from under it. The inversion check is the secondary
                // guard, and applies only to a real range: a replace whose
                // window collapsed or inverted has lost its meaning even if
                // neither end reads as deletedAcross.
                const anchorDestroyed = fromR.deletedAcross || toR.deletedAcross
                const rangeInvalid = !collapsed && from >= to
                if (anchorDestroyed || rangeInvalid) {
                  /* Reposition instead of drop: a whole-doc replace collapses
                   * the PM anchor, but a proposal carrying a Yjs relative anchor
                   * can be re-derived post-settle. Keep it, flagged stale, at
                   * its old (clamped) spot so the decoration stays painted — the
                   * anti-flash window — until it is re-placed (by
                   * `settleAIProposals` or, failing that, by the reconciler's
                   * op re-resolution). Without a relative anchor (non-collab editor)
                   * there is nothing to recover from: preserve the legacy drop. */
                  if (wholeDoc && p.relAnchor) {
                    remapped.set(id, { ...p, stale: true })
                  }
                  continue
                }

                remapped.set(id, { ...p, from, to, stale: false })
              }
              proposals = remapped
            }

            if (meta?.clear) {
              return { proposals: new Map(), decorations: DecorationSet.empty }
            }

            if (meta?.add) {
              const next = new Map(proposals)
              for (const p of meta.add) {
                // Capture the Strategy A anchor + structural fingerprint against
                // the CURRENT doc/binding at stage time. Returns undefined for a
                // non-collab editor (no y-prosemirror binding), leaving the
                // proposal on the legacy drop-on-collapse path.
                const relAnchor =
                  captureRelAnchor(newState, p.from, p.to) ?? undefined
                const fingerprint = relAnchor
                  ? captureFingerprint(newState.doc, p.from, p.render)
                  : undefined
                next.set(p.id, { ...p, relAnchor, fingerprint, stale: false })
              }
              proposals = next
            }

            if (meta?.supersede) {
              const { supersededId, replacement, ownRange } = meta.supersede
              const prevProposal = proposals.get(supersededId)
              // Defensive: the command already checked this, but `apply` can
              // run against a state the command did not read.
              if (prevProposal && ownRange) {
                /* A proposal that displaces another on its line covers its own
                 * span, so it is anchored where it resolved, as an added one
                 * is, and only takes the superseded one's place in the map. */
                const relAnchor =
                  captureRelAnchor(
                    newState,
                    replacement.from,
                    replacement.to
                  ) ?? undefined
                const fingerprint = relAnchor
                  ? captureFingerprint(
                      newState.doc,
                      replacement.from,
                      replacement.render
                    )
                  : undefined
                const next = new Map(proposals)
                next.set(replacement.id, {
                  ...replacement,
                  relAnchor,
                  fingerprint,
                  stale: false,
                  supersedes: supersededId,
                })
                next.delete(supersededId)
                proposals = next
              } else if (prevProposal) {
                const next = new Map(proposals)
                // Inherit the anchor VERBATIM — same relAnchor/fingerprint
                // object references as the superseded proposal, never
                // re-captured. Re-capturing (what `addAIProposals` does above,
                // against `newState`) would re-derive the position from the
                // current doc/text instead of preserving where the original
                // proposal actually was. `stale` is carried through unchanged
                // too: the replacement inherits the superseded proposal's
                // staleness rather than resetting it.
                next.set(replacement.id, {
                  ...replacement,
                  from: prevProposal.from,
                  to: prevProposal.to,
                  relAnchor: prevProposal.relAnchor,
                  fingerprint: prevProposal.fingerprint,
                  stale: prevProposal.stale,
                  supersedes: supersededId,
                })
                next.delete(supersededId)
                proposals = next
              }
            }

            if (meta?.remove) {
              const next = new Map(proposals)
              for (const id of meta.remove) {
                next.delete(id)
              }
              proposals = next
            }

            if (meta?.edited) {
              /* Content-only update: the anchor, relative anchor and staleness
               * are untouched, so an island edit never re-anchors the card it
               * belongs to. */
              const p = proposals.get(meta.edited.id)
              if (p) {
                const next = new Map(proposals)
                next.set(p.id, {
                  ...p,
                  editedContent: meta.edited.editedContent,
                })
                proposals = next
              }
            }

            if (meta?.settle) {
              // Post-settle re-derivation (never synchronous with the collapse —
              // computed by `settleAIProposals` after the binding reconciles).
              // Only the proposals whose anchors re-derived have an entry; the
              // rest are left stale for the reconciler.
              const next = new Map(proposals)
              for (const [id, update] of meta.settle) {
                const p = next.get(id)
                if (!p) {
                  continue
                }
                next.set(id, {
                  ...p,
                  from: update.from,
                  to: update.to,
                  stale: false,
                })
              }
              proposals = next
            }

            const decorations =
              meta || tr.docChanged
                ? buildDecorations(
                    newState.doc,
                    proposals,
                    shells,
                    inlineChoiceSources
                  )
                : prev.decorations

            return { proposals, decorations }
          },
        },
        /* Drives the widget-shell cache off plugin-state reconciliation: after
         * every transaction, hand the shell manager the ids that currently paint
         * an insert widget so it can tear down shells for affirmatively-removed
         * proposals (microtask-deferred) while reusing the rest across the
         * whole-doc-replace drop/re-add regime. */
        view() {
          return {
            update(view) {
              if (!shells) {
                return
              }
              const state = AI_PROPOSALS_PLUGIN_KEY.getState(view.state)
              const ids = new Set<string>()
              if (state) {
                for (const [id, p] of state.proposals) {
                  if (
                    widgetProposal(p) ||
                    (inlineChoiceSources && choiceSourceProposal(p))
                  ) {
                    ids.add(id)
                  }
                }
                // Refresh already-mounted shells to the latest content: a keyed
                // widget's DOM is reused without re-running getShell when a
                // streamed op completes, so a shell that first painted a partial
                // op would otherwise stay frozen at the fragment.
                shells.syncProps(state.proposals)
              }
              shells.reconcile(ids)
            },
            destroy() {
              shells?.destroyAll()
            },
          }
        },
        props: {
          decorations(state) {
            return AI_PROPOSALS_PLUGIN_KEY.getState(state)?.decorations
          },
        },
      }),
    ]
  },

  addCommands() {
    return {
      addAIProposals:
        (proposals: AIProposal[]) =>
        ({ tr, dispatch }) => {
          if (proposals.length === 0) {
            return false
          }
          if (dispatch) {
            tr.setMeta(AI_PROPOSALS_PLUGIN_KEY, {
              add: proposals,
            } satisfies Meta)
          }
          return true
        },

      supersedeAIProposal:
        (
          supersededId: string,
          replacement: AIProposal,
          options?: { ownRange?: boolean }
        ) =>
        ({ state, tr, dispatch }) => {
          const ps = AI_PROPOSALS_PLUGIN_KEY.getState(state)
          if (!ps?.proposals.has(supersededId)) {
            return false
          }
          if (dispatch) {
            tr.setMeta(AI_PROPOSALS_PLUGIN_KEY, {
              supersede: {
                supersededId,
                replacement,
                ...(options?.ownRange && { ownRange: true as const }),
              },
            } satisfies Meta)
          }
          return true
        },

      acceptAIProposal:
        (id: string) =>
        ({ state, chain }) => {
          const ps = AI_PROPOSALS_PLUGIN_KEY.getState(state)
          const p = ps?.proposals.get(id)
          if (!p) {
            return false
          }
          const target =
            p.kind === 'replace' ? { from: p.from, to: p.to } : p.from
          return chain()
            .insertContentAt(target, p.htmlContent)
            .command(({ tr }) => {
              tr.setMeta(AI_PROPOSALS_PLUGIN_KEY, {
                remove: [id],
              } satisfies Meta)
              return true
            })
            .run()
        },

      rejectAIProposal:
        (id: string) =>
        ({ tr, dispatch }) => {
          if (dispatch) {
            tr.setMeta(AI_PROPOSALS_PLUGIN_KEY, {
              remove: [id],
            } satisfies Meta)
          }
          return true
        },

      /* Mirror a suggestion's island edit onto its painted proposal, so the card
       * shows what an accept would insert the moment the island closes — the
       * persisted `editedContent` write is async and the next re-paint may be
       * many edits away. `undefined` clears it (Restore original). A no-op when
       * the content already matches, so the debounced write that fires on every
       * few keystrokes cannot loop the host editor. */
      setAIProposalEditedContent:
        (id: string, editedContent: string | undefined) =>
        ({ state, tr, dispatch }) => {
          const p = AI_PROPOSALS_PLUGIN_KEY.getState(state)?.proposals.get(id)
          if (!p || p.editedContent === editedContent) {
            return false
          }
          if (dispatch) {
            tr.setMeta(AI_PROPOSALS_PLUGIN_KEY, {
              edited: { id, editedContent },
            } satisfies Meta)
          }
          return true
        },

      rejectAllAIProposals:
        () =>
        ({ state, tr, dispatch }) => {
          const ps = AI_PROPOSALS_PLUGIN_KEY.getState(state)
          if (!ps || ps.proposals.size === 0) {
            return false
          }
          if (dispatch) {
            tr.setMeta(AI_PROPOSALS_PLUGIN_KEY, { clear: true } satisfies Meta)
          }
          return true
        },

      clearAIProposalsByToolCall:
        (toolCallId: string) =>
        ({ state, tr, dispatch }) => {
          const ps = AI_PROPOSALS_PLUGIN_KEY.getState(state)
          if (!ps) {
            return false
          }
          const ids: string[] = []
          for (const [id, p] of ps.proposals) {
            if (p.toolCallId === toolCallId) {
              ids.push(id)
            }
          }
          if (ids.length === 0) {
            return false
          }
          if (dispatch) {
            tr.setMeta(AI_PROPOSALS_PLUGIN_KEY, {
              remove: ids,
            } satisfies Meta)
          }
          return true
        },

      /* Post-settle reposition pass (Strategy A). Called by the reconciler in
       * the same task as a whole-doc-replace transaction, AFTER y-prosemirror
       * has reconciled its binding — never synchronously inside the collapse's
       * `apply`. Re-derives each stale proposal's PM range from its Yjs relative
       * anchor (gated by the structural fingerprint) and dispatches a `settle`
       * meta repositioning the ones that re-derived. Returns true iff at least
       * one did, so the caller knows Strategy A owned the whole-doc replace and
       * can skip the content-re-resolution fallback.
       *
       * A proposal whose anchor does NOT re-derive is LEFT STALE rather than
       * dropped, because an unresolvable anchor is not evidence its content is
       * gone. The anchor is captured once at stage time, so any later edit that
       * restructures the note — accepting a neighbouring proposal splits the
       * paragraph the anchor rode — leaves it pointing at Yjs nodes
       * y-prosemirror has since rebuilt, while the text it targets is still
       * right there. The caller re-resolves those from their own op (Strategy B)
       * and drops only what neither strategy can place. */
      settleAIProposals:
        () =>
        ({ state, tr, dispatch }) => {
          const ps = AI_PROPOSALS_PLUGIN_KEY.getState(state)
          if (!ps) {
            return false
          }
          const settle = new Map<string, SettleUpdate>()
          for (const [id, p] of ps.proposals) {
            if (!p.stale) {
              continue
            }
            const resolved = p.relAnchor
              ? resolveRelAnchor(state, p.relAnchor, p.fingerprint)
              : null
            if (resolved) {
              settle.set(id, resolved)
            }
          }
          if (settle.size === 0) {
            return false
          }
          if (dispatch) {
            tr.setMeta(AI_PROPOSALS_PLUGIN_KEY, { settle } satisfies Meta)
          }
          return true
        },
    }
  },
})

// A proposal that paints an insert-preview widget — everything except the
// inline choice-option highlight, which paints a node decoration and no widget.
export function widgetProposal(p: AIProposal): boolean {
  return p.render.type !== 'choiceOption'
}

/* A choice pick that cited something. It still paints no preview card, so
 * `widgetProposal` stays false for it, but its source is shown in a widget of
 * its own under the line that holds the field. A pick with no provenance paints
 * exactly what it did before. */
export function choiceSourceProposal(p: AIProposal): boolean {
  return (
    p.render.type === 'choiceOption' &&
    ((p.sourceProvenance?.length ?? 0) > 0 || p.citation != null)
  )
}

// Two picks citing the same thing share one source line rather than repeating it.
function sourceIdentity(p: AIProposal): string {
  return JSON.stringify([p.sourceProvenance ?? null, p.citation ?? null])
}

const EMPTY_LIST_TYPES = new Set(['bulletList', 'orderedList'])

// Whether `from`–`to` is exactly one list holding no text or inline node.
function isEmptyList(doc: PMNode, from: number, to: number): boolean {
  const node = doc.nodeAt(from)
  if (
    !node ||
    from + node.nodeSize !== to ||
    !EMPTY_LIST_TYPES.has(node.type.name)
  ) {
    return false
  }
  let empty = true
  node.descendants((child) => {
    empty &&= !child.isInline
    return empty
  })
  return empty
}

function buildDecorations(
  doc: PMNode,
  proposals: Map<string, AIProposal>,
  shells: SuggestionWidgetShells | null,
  inlineChoiceSources: boolean
): DecorationSet {
  const decorations: Decoration[] = []
  // Painted choice picks that cited a source, by the end of the line holding
  // their field.
  const choiceSources = new Map<number, AIProposal[]>()
  for (const [id, p] of proposals) {
    // A stale proposal is briefly painted at its pre-collapse spot (the
    // anti-flash window) before `settleAIProposals` repositions it; clamp so an
    // old position past the end of the shrunken post-replace doc can't throw.
    const from = p.stale ? Math.min(p.from, doc.content.size) : p.from
    const to = p.stale ? Math.min(p.to, doc.content.size) : p.to
    if (p.render.type === 'choiceOption') {
      const node = doc.nodeAt(from)
      // Inline-highlight the matching option button, but only for an UNSELECTED
      // choice field. A choice op must NEVER paint the default insert preview:
      // when the target isn't an unselected choice node (the field got selected
      // while the proposal was pending, or the anchor drifted off the node),
      // paint nothing — the proposal stays pending and unpainted until
      // re-resolution either drops it to the unplaced count or the field is
      // unselected again.
      if (node?.type.name === 'choice' && !node.attrs.display) {
        decorations.push(
          Decoration.node(
            from,
            to,
            {},
            {
              aiChoiceProposal: {
                id,
                value: p.render.value,
                label: p.label ?? null,
              } satisfies AIChoiceProposalSpec,
            }
          )
        )
        const line = doc.resolve(from)
        if (
          shells &&
          inlineChoiceSources &&
          choiceSourceProposal(p) &&
          line.parent.isTextblock
        ) {
          const lineEnd = line.end()
          choiceSources.set(lineEnd, [...(choiceSources.get(lineEnd) ?? []), p])
        }
      }
      continue
    }
    if (p.kind === 'replace' && isEmptyList(doc, from, to)) {
      /* The empty bullet a list answer takes the place of has no text to
       * strike, so it is hidden: the card after it shows the list that will
       * stand there, as accepting leaves it. */
      decorations.push(Decoration.node(from, to, { style: 'display: none' }))
    } else if (p.kind === 'replace' && from < to) {
      /* A fill-only replace strikes only what its replacement drops: text it
       * restates word for word stays as it reads. */
      const fill = p.fillOnly
        ? splitFillReplacement(
            doc,
            from,
            to,
            effectiveSuggestionHtml(p, p.htmlContent)
          )
        : null
      const removalAttrs: DecorationAttrs = {
        class: AI_PROPOSAL_REMOVAL_CLASS,
        nodeName: 'span',
        'data-ai-proposal-id': id,
      }
      if (p.label) {
        removalAttrs['data-ai-proposal-label'] = p.label
      }
      for (const range of fill?.dropped ?? [{ from, to }]) {
        decorations.push(Decoration.inline(range.from, range.to, removalAttrs))
      }
    }
    /* The insert preview is host-owned: paint the widget only when a React host
     * has injected a shell manager. No host → no widget, never innerHTML. The
     * shell returned by getShell renders the proposal through the real node
     * views (SuggestionContentPreview) and is cached across drop/re-add. */
    if (shells) {
      decorations.push(
        Decoration.widget(to, () => shells.getShell(p), {
          side: 1,
          ignoreSelection: true,
          /* Keystroke/selection containment (design §2.a): the widget hosts a
           * real editable island (its own local editor). Without this the host
           * editor handles DOM events bubbling out of the widget — hijacking
           * keystrokes and selection while the clinician is editing the island.
           * ignoreSelection already keeps the host from re-syncing selection
           * INTO the widget; stopEvent keeps events FROM the widget out of the
           * host. The island's own editor still handles its events (separate
           * view), so this suppresses only the host's double-handling. */
          stopEvent: () => true,
          key: `ai-proposal-widget-${id}`,
        })
      )
    }
  }
  if (shells) {
    for (const [lineEnd, picks] of choiceSources) {
      /* One widget per distinct source, at the end of the field's line, as the
       * insert preview sits at the end of the text it follows: an inline field
       * has no block of its own to put a line under. Document order, so the
       * sources read in the order of the fields they belong to. A later pick
       * citing what an earlier one on the line already shows adds nothing;
       * working that out from the picks still pending means rejecting the
       * earlier one hands the line to the next. */
      const shown = new Set<string>()
      for (const p of orderBy(picks, (pick) => pick.from, 'asc')) {
        const identity = sourceIdentity(p)
        if (shown.has(identity)) {
          continue
        }
        shown.add(identity)
        decorations.push(
          Decoration.widget(lineEnd, () => shells.getShell(p), {
            side: 1,
            ignoreSelection: true,
            // As the insert widget's: events from it stay out of the host.
            stopEvent: () => true,
            key: `ai-proposal-choice-source-${p.id}`,
          })
        )
      }
    }
  }
  return decorations.length > 0
    ? DecorationSet.create(doc, decorations)
    : DecorationSet.empty
}
